import React from 'react';
import useGameStore from '../engine/store';
import { getPersonality } from '../engine/opponents';

const MOOD_EMOJIS = {
  neutral: '😐', pleased: '😊', hostile: '😠', angry: '😤',
  nervous: '😰', thinking: '🤔', firm: '😑', suspicious: '🧐',
  desperate: '😱', defiant: '💪', warm: '😄', friendly_suspicious: '🤨',
  displeased: '😒', satisfied: '😌', urgent: '⏰',
};

const MOOD_COLORS = {
  neutral: '#94a3b8', pleased: '#00ff88', hostile: '#ff3366', angry: '#ff0000',
  nervous: '#ffcc00', thinking: '#00aaff', firm: '#ff8800', suspicious: '#aa55ff',
  desperate: '#ff3366', defiant: '#ff8800', warm: '#00ff88', friendly_suspicious: '#ffcc00',
  displeased: '#ff8800', satisfied: '#00ff88', urgent: '#ff3366',
};

export default function OpponentPanel() {
  const { personalityData, opponentName, opponentMood, opponentTyping, opponentTrust, personality } = useGameStore();

  if (!personalityData) return null;

  const p = personalityData;
  const moodEmoji = MOOD_EMOJIS[opponentMood] || '😐';
  const moodColor = MOOD_COLORS[opponentMood] || '#94a3b8';

  // Trust bar
  const trustPct = Math.round(opponentTrust * 100);
  const trustColor = trustPct > 70 ? '#00ff88' : trustPct > 40 ? '#ffcc00' : '#ff3366';

  return (
    <div className="glass-panel p-4">
      {/* Avatar and name */}
      <div className="flex items-center gap-4 mb-4">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl border-2 transition-all duration-500"
          style={{
            borderColor: `${p.color}60`,
            background: `${p.color}10`,
            boxShadow: opponentTyping ? `0 0 20px ${p.color}30` : 'none',
          }}
        >
          {opponentTyping ? (
            <span className="animate-pulse">💬</span>
          ) : (
            p.avatar
          )}
        </div>
        <div className="flex-1">
          <div className="font-bold text-white text-lg">{opponentName}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: `${p.color}20`, color: p.color }}
            >
              {p.emoji} {p.label}
            </span>
          </div>
        </div>
        {/* Mood */}
        <div className="text-center">
          <div className="text-3xl transition-all duration-300" style={{ filter: `drop-shadow(0 0 8px ${moodColor}50)` }}>
            {moodEmoji}
          </div>
          <div className="text-xs mt-0.5 capitalize" style={{ color: moodColor }}>{opponentMood}</div>
        </div>
      </div>

      {/* Traits */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {p.traits.map((trait, i) => (
          <span key={i} className="text-xs px-2 py-1 rounded-md bg-dark-600/60 text-gray-400 border border-white/5">
            {trait}
          </span>
        ))}
      </div>

      {/* Trust meter */}
      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-500">Trust Level</span>
          <span className="font-mono" style={{ color: trustColor }}>{trustPct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-dark-600 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${trustPct}%`, background: trustColor }}
          />
        </div>
      </div>

      {/* Hidden stats (player can infer) */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-xs text-gray-600">Patience</div>
          <div className="flex justify-center gap-0.5 mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1.5 h-4 rounded-sm transition-all"
                style={{
                  background: i < Math.round(p.patience * 5) ? p.color : '#243044',
                }}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600">Risk</div>
          <div className="flex justify-center gap-0.5 mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1.5 h-4 rounded-sm transition-all"
                style={{
                  background: i < Math.round(p.riskTolerance * 5) ? '#ff8800' : '#243044',
                }}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-600">Bluff Def</div>
          <div className="flex justify-center gap-0.5 mt-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1.5 h-4 rounded-sm transition-all"
                style={{
                  background: i < Math.round(p.bluffResistance * 5) ? '#aa55ff' : '#243044',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
